<template>
  <Text v-if="followingProductLinks?.length">
    {{ $textDictionary['pdp.hint.discontinued.followingproducts.before'] }}
    <div>
      <div :class="$style.followingProducts">
        <ul>
          <li v-for="item in followingProductLinks" :key="item.url">
            <LinkElement :link="item.url">{{ item.label }}</LinkElement>
          </li>
        </ul>
      </div>
    </div>
  </Text>
  <LibRichTextRenderer
    v-if="afterDiscontinuedHint"
    :rich-text="afterDiscontinuedHint"
  />
</template>

<script lang="ts" setup>
import { LinkElement, Text } from '@hypercodestudio/basler-components';
import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';
import type { Document } from '@contentful/rich-text-types';
import { isDefined } from '~/utils/guards/isDefined';

const store = useProductDetailPageStore();
const { $textDictionary, $dictionary, $activeLocale } = useNuxtApp();
const logger = useLogger();

const afterDiscontinuedHint = computed((): Document | undefined => {
  return (
    $dictionary.value['pdp.hint.discontinued.followingproducts.after'] ??
    undefined
  );
});

type FollowingProductLinksModel = {
  label: string;
  url: string;
};

const { data: followingProductLinks } = await useAsyncData<
  FollowingProductLinksModel[]
>(
  `followingProductLinks-${store.decoratedProduct?.id}`,
  async () => {
    const entries: (FollowingProductLinksModel | undefined)[] =
      await Promise.all(
        store.decoratedProduct?.discontinuationLinks.map(async (link) => {
          try {
            const response = await $fetch(
              `/api/contentful/slugById/${link.ctf_entity_id?.value[0]}?locale=${$activeLocale.value.contentfulCode}`
            );

            return {
              label:
                $textDictionary.value[
                  'pdp.hint.discontinued.followingproducts.link.label'
                ] ?? 'To Seriespage',
              url: buildUrlString(
                $activeLocale.value.webapp,
                response.slug,
                link.parameters?.value[0].replace('?', '')
              )
            };
          } catch (e) {
            logger.error('failed to retrieve following product links', e);
          }
        }) ?? []
      );

    store.decoratedProduct?.followingProducts.forEach((followingProduct) => {
      if (!followingProduct.url_key) {
        logger.error(
          'following product links has no url key and will be ignored',
          followingProduct
        );

        return;
      }

      entries.push({
        label: followingProduct.name ?? '',
        url: buildPdpUrl($activeLocale.value.webapp, followingProduct.url_key)
      });
    });

    return entries.filter(isDefined);
  },
  { watch: [() => store.decoratedProduct, $activeLocale, $textDictionary] }
);
</script>

<style module>
.followingProducts {
  padding: 5px;
  display: inline-block;
}
</style>
